import React, { useState } from "react";
import { round } from "lodash";
import { Copy, Loader2 } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";

import { foNavNumberFormat } from "./utils";

const navFormat = foNavNumberFormat("# ##0");
const impactFormat = foNavNumberFormat("0.00%");

export interface FoNavKpiValues {
    prevNav: number | null;
    prevNavDate: string | null;
    prevNavBeforeOrdersBeforeFee: number | null;
    estimatedChangePct: number | null;
    estimatedNav: number | null;
    estimateLoading: boolean;
    actualChangePct: number | null;
    actualNav: number | null;
    actualDate: string | null;
}

export type FoNavEstimateKpiValues = Pick<FoNavKpiValues, "estimatedChangePct" | "estimatedNav" | "estimateLoading">;

interface FoNavKpisProps {
    kpis: FoNavKpiValues | null;
}

function formatNav(value: number | null): string {
    if (value === null) {
        return "—";
    }
    return navFormat(round(value, 0)) ?? "—";
}

function formatSignedPct(value: number | null): string {
    if (value === null) {
        return "—";
    }
    const formatted = impactFormat(value) ?? "—";
    if (value > 0) {
        return `+${formatted}`;
    }
    return formatted;
}

function formatSignedNav(value: number | null): string {
    if (value === null) {
        return "—";
    }
    const formatted = navFormat(round(value, 0)) ?? "—";
    if (value > 0) {
        return `+${formatted}`;
    }
    return formatted;
}

function changeTone(value: number | null): "neutral" | "positive" | "negative" {
    if (value === null || value === 0) {
        return "neutral";
    }
    if (value > 0) {
        return "positive";
    }
    return "negative";
}

function FoNavCopyButton({ id, text }: { id: string; text: string }): React.ReactElement {
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleCopy = () => {
        if (window["clipboardData"] && window["clipboardData"].setData) {
            setTooltipOpen(true);
            setTimeout(() => {
                setTooltipOpen(false);
            }, 2000);
            window["clipboardData"].setData("Text", text);
            return;
        }

        if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
            const textarea = document.createElement("textarea");
            textarea.textContent = text;
            textarea.style.position = "fixed";
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand("copy");
            } catch (ex) {
                console.warn("Copy to clipboard failed.", ex);
            } finally {
                setTooltipOpen(true);
                setTimeout(() => {
                    setTooltipOpen(false);
                }, 2000);
                document.body.removeChild(textarea);
            }
        }
    };

    return (
        <Tooltip.Provider>
            <Tooltip.Root open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <Tooltip.Trigger asChild>
                    <button
                        id={id}
                        type="button"
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                        title="Copy to clipboard"
                        onClick={handleCopy}
                    >
                        <Copy className="w-5 h-5" strokeWidth={1.75} />
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content className="px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg z-50" side="bottom">
                        Copied!
                        <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
}

function KpiCard({
    label,
    value,
    hint,
    tone = "neutral",
    copyId,
    loading = false,
    loadingHint = "Loading risk factors…"
}: {
    label: string;
    value: string;
    hint?: string;
    tone?: "neutral" | "positive" | "negative";
    copyId?: string;
    loading?: boolean;
    loadingHint?: string;
}): React.ReactElement {
    const valueColor =
        tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-rose-700" : "text-slate-900";

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-start justify-between gap-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
                {copyId && !loading && value !== "—" ? <FoNavCopyButton id={copyId} text={value} /> : null}
            </div>
            {loading ? (
                <div className="mt-1 inline-flex items-center gap-2 text-lg font-medium text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.75} />
                    <span>Calculating…</span>
                </div>
            ) : (
                <div className={`mt-1 text-2xl font-semibold tabular-nums leading-tight ${valueColor}`}>{value}</div>
            )}
            {loading ? <div className="mt-1 text-xs text-slate-500">{loadingHint}</div> : null}
            {!loading && hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
        </div>
    );
}

export function FoNavKpis({ kpis }: FoNavKpisProps): React.ReactElement {
    const estimatedChangeSek =
        kpis && kpis.estimatedNav !== null && kpis.prevNav !== null ? kpis.estimatedNav - kpis.prevNav : null;
    const actualChangeSek =
        kpis && kpis.actualNav !== null && kpis.prevNavBeforeOrdersBeforeFee !== null
            ? kpis.actualNav - kpis.prevNavBeforeOrdersBeforeFee
            : null;

    let prevNavHint = "After orders after fee";
    if (kpis?.prevNavDate) {
        prevNavHint = `After orders after fee · ${kpis.prevNavDate}`;
    }

    return (
        <div className="grid [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))] gap-4 mb-4">
            <KpiCard label="Prev NAV" value={formatNav(kpis?.prevNav ?? null)} hint={prevNavHint} />
            <KpiCard
                label="Estimated chg"
                value={formatSignedPct(kpis?.estimatedChangePct ?? null)}
                hint={formatSignedNav(estimatedChangeSek)}
                tone={changeTone(kpis?.estimatedChangePct ?? null)}
                loading={kpis?.estimateLoading ?? false}
            />
            <KpiCard
                label="New NAV"
                value={formatNav(kpis?.estimatedNav ?? null)}
                hint="Estimated"
                copyId="fonav-new-nav"
                loading={kpis?.estimateLoading ?? false}
            />
            {kpis?.actualDate ? (
                <>
                    <KpiCard
                        label="Actual"
                        value={formatSignedPct(kpis.actualChangePct)}
                        hint={formatSignedNav(actualChangeSek)}
                        tone={changeTone(kpis.actualChangePct)}
                    />
                    <KpiCard label="Actual" value={formatNav(kpis.actualNav)} hint={`Before orders before fee · ${kpis.actualDate}`} />
                </>
            ) : null}
        </div>
    );
}
